// Shared helpers for consistent user identity display across every history
// table, dashboard card, and PDF/email receipt.
//
// The product rule is: whenever an Agent or Master Agent (or admin/collaborator)
// is referenced, always show their NAME followed by the ROLE LABEL and their
// AGENT ID (agent_code). We never fall back to a bare UUID when a user exists.

const ROLE_LABELS = {
  MASTER_ADMIN: "Master Admin",
  MASTER_AGENT: "Master Agent",
  AGENT: "Agent",
  COLLABORATOR: "Collaborator",
  CASH_VERIFIER: "Cash Verifier",
};

/** Human-readable role label ("Master Agent" instead of "MASTER_AGENT"). */
export function roleLabel(role) {
  if (!role) return "";
  return ROLE_LABELS[String(role).toUpperCase()] || String(role);
}

/**
 * Format a user reference as "Name — Role ID #Code".
 * Examples:
 *   { name: "Ana", agent_code: "10010", role: "AGENT" }
 *      → "Ana — Agent ID #10010"
 *   { username: "dada", agent_code: "10008", role: "MASTER_AGENT" }
 *      → "dada — Master Agent ID #10008"
 *   { username: "admin", role: "MASTER_ADMIN" }
 *      → "admin — Master Admin"      (no code available)
 */
export function formatUserLabel(u) {
  if (!u) return "—";
  const name = (u.name && u.name.trim()) || u.username || "—";
  const role = roleLabel(u.role);
  const code = u.agent_code ? `ID #${u.agent_code}` : "";
  const parts = [name, role, code].filter(Boolean);
  // Format:  "Name — Role ID #Code"
  if (parts.length === 3) return `${parts[0]} — ${parts[1]} ${parts[2]}`;
  if (parts.length === 2) return `${parts[0]} — ${parts[1]}`;
  return parts[0] || "—";
}

/** Same as formatUserLabel but returns split fragments for JSX rendering
 * (name in bold, role+id muted). Useful when tables want two-line layout. */
export function splitUserLabel(u) {
  if (!u) return { name: "—", meta: "" };
  const name = (u.name && u.name.trim()) || u.username || "—";
  const role = roleLabel(u.role);
  const code = u.agent_code ? `ID #${u.agent_code}` : "";
  const meta = [role, code].filter(Boolean).join(" ");
  return { name, meta };
}

/** Build a user-ref object from any raw record shape. Callers can spread
 * arbitrary transfer/topup rows and pick the right side. */
export function pickParty(row, side) {
  if (!row) return null;
  const prefix = side === "recipient"   ? "recipient_"    :
                  side === "sender"     ? "sender_"       :
                  side === "actor"      ? "actor_"        :
                  side === "counterparty" ? "counterparty_" :
                  side === "owner"      ? "owner_"        :
                  side === "agent"      ? "agent_"        : "";
  return {
    name:       row[`${prefix}name`],
    username:   row[`${prefix}username`],
    agent_code: row[`${prefix}agent_code`],
    role:       row[`${prefix}role`],
    email:      row[`${prefix}email`],
    country:    row[`${prefix}country`],
  };
}

/** Compact ref formatting for tables (keep long ref for details/receipt). */
export function shortRef(ref, len = 10) {
  if (!ref) return "—";
  const clean = String(ref).replace(/-/g, "").toUpperCase();
  return clean.slice(0, len);
}
